import { toast } from 'sonner';
import type { ExternalToast } from 'sonner';

/**
 * Toast duration constants (in milliseconds)
 */
export const TOAST_DURATIONS = {
  SUCCESS: 3000, // 3 seconds
  ERROR: 5000, // 5 seconds
  INFO: 4000, // 4 seconds
  WARNING: 4000, // 4 seconds
  LOADING: Infinity, // Until manually dismissed
} as const;

/**
 * Enhanced success toast with consistent duration
 */
// eslint-disable-next-line sonarjs/function-return-type
export function toastSuccess(
  message: string,
  options?: Omit<ExternalToast, 'duration'>,
): string | number {
  return toast.success(message, {
    duration: TOAST_DURATIONS.SUCCESS,
    ...options,
  });
}

/**
 * Enhanced error toast with consistent duration
 */
// eslint-disable-next-line sonarjs/function-return-type
export function toastError(
  message: string,
  options?: Omit<ExternalToast, 'duration'>,
): string | number {
  return toast.error(message, {
    duration: TOAST_DURATIONS.ERROR,
    ...options,
  });
}

/**
 * Success toast with undo action
 */

type ToastUndoOptions = Omit<ExternalToast, 'duration' | 'action'> & {
  undoLabel?: string;
};

// eslint-disable-next-line sonarjs/function-return-type
export function toastSuccessWithUndo(
  message: string,
  onUndo: () => void | Promise<void>,
  options?: ToastUndoOptions,
): string | number {
  const { undoLabel, ...toastOptions } = options ?? {};
  return toast.success(message, {
    duration: TOAST_DURATIONS.SUCCESS,
    action: {
      label: undoLabel ?? 'Undo',
      onClick: () => {
        const result = onUndo();
        if (result instanceof Promise) {
          result.catch(console.error);
        }
      },
    },
    ...toastOptions,
  });
}

/**
 * Delete toast with undo action
 */
// eslint-disable-next-line sonarjs/function-return-type
export function toastDeleteWithUndo(
  message: string,
  onUndo: () => void | Promise<void>,
  undoLabel = 'Undo',
  options?: Omit<ExternalToast, 'duration' | 'action'>,
): string | number {
  return toast.success(message, {
    duration: TOAST_DURATIONS.SUCCESS,
    action: {
      label: undoLabel,
      onClick: () => {
        const result = onUndo();
        if (result instanceof Promise) {
          result.catch(console.error);
        }
      },
    },
    ...options,
  });
}

/**
 * Promise-based toast for async operations
 */
export function toastPromise<T>(
  promise: Promise<T>,
  messages: {
    loading: string;
    success: string | ((data: T) => string);
    error: string | ((error: unknown) => string);
  },
  options?: Omit<ExternalToast, 'duration'>,
): Promise<T> {
  toast.promise(promise, {
    loading: messages.loading,
    success: messages.success,
    error: messages.error,
    duration: TOAST_DURATIONS.SUCCESS,
    ...options,
  });
  return promise;
}
